import React, { useState, useEffect } from "react";
import { 
  Facebook, 
  Twitter, 
  Instagram, 
  Linkedin, 
  Youtube, 
  Music,
  X,
  Loader2
} from "lucide-react";
import { Button } from "../../components/ui/Button";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { 
  fetchSocialLinkByType,
  createSocialLink, 
  updateSocialLink 
} from "../../redux/Slices/contactInfoSlice";
import { SocialLinkTypeEnum } from "../../modules/shared/model/contact-info.interfaces";
import { useSearchParams } from "react-router-dom";

interface SocialMediaUrls {
  facebook: string;
  twitter: string;
  instagram: string;
  linkedin: string;
  youtube: string;
  tiktok: string;
}

interface SocialMediaModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SocialMediaModal: React.FC<SocialMediaModalProps> = ({
  isOpen,
  onClose
}) => {
  const dispatch = useAppDispatch();
  const { socialLinks } = useAppSelector((state) => state.contactInfos);
  const [searchParams] = useSearchParams();
  
  // Get type from search params or default to Terms_Conditions
  const typeParam = searchParams.get("type");
  const type = (typeParam && Object.values(SocialLinkTypeEnum).includes(Number(typeParam) as SocialLinkTypeEnum)) 
    ? Number(typeParam) as SocialLinkTypeEnum 
    : SocialLinkTypeEnum.Terms_Conditions;
  
  // Default social media URLs
  const defaultUrls: SocialMediaUrls = {
    facebook: "https://web.facebook.com/profile.php?id=61567111596784",
    twitter: "https://x.com/Alldresskokobeo",
    instagram: "https://www.instagram.com/serviceskokobeo/",
    linkedin: "",
    youtube: "",
    tiktok: ""
  };
  
  const [urls, setUrls] = useState<SocialMediaUrls>({ ...defaultUrls });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load data when modal opens
  useEffect(() => {
    if (isOpen) {
      if (socialLinks.current) {
        const apiUrls = {
          facebook: socialLinks.current.facebook || "",
          twitter: socialLinks.current.twitter || "",
          instagram: socialLinks.current.instagram || "",
          linkedin: socialLinks.current.linkedin || "",
          youtube: socialLinks.current.youtube || "",
          tiktok: socialLinks.current.tiktok || ""
        };
        setUrls(apiUrls);
      } else {
        setUrls({ ...defaultUrls });
      }
      setError(null);
    }
  }, [isOpen, socialLinks.current]);

  const handleSave = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (socialLinks.current) {
        // Update existing social link
        await dispatch(updateSocialLink({
          id: socialLinks.current.id,
          data: {
            facebook: urls.facebook,
            twitter: urls.twitter,
            instagram: urls.instagram,
            linkedin: urls.linkedin,
            youtube: urls.youtube,
            tiktok: urls.tiktok,
            ...(type && { type: { id: type } })
          }
        })).unwrap();
      } else {
        // Create new social link
        await dispatch(createSocialLink({
          facebook: urls.facebook,
          twitter: urls.twitter,
          instagram: urls.instagram,
          linkedin: urls.linkedin,
          youtube: urls.youtube,
          tiktok: urls.tiktok,
          ...(type && { type: { id: type } })
        })).unwrap();
      }

      onClose();
    } catch (err: any) {
      setError(err.message || "Failed to save social links");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (socialLinks.current) {
      const apiUrls = {
        facebook: socialLinks.current.facebook || "",
        twitter: socialLinks.current.twitter || "",
        instagram: socialLinks.current.instagram || "",
        linkedin: socialLinks.current.linkedin || "",
        youtube: socialLinks.current.youtube || "",
        tiktok: socialLinks.current.tiktok || ""
      };
      setUrls(apiUrls);
    } else {
      setUrls({ ...defaultUrls });
    }
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" style={{ textAlign: "left" }}>
      <div className="bg-white rounded-xl w-full max-w-2xl p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Social Media URLs</h2>
          <button onClick={handleCancel}><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
              <Facebook className="h-5 w-5 mr-2 text-blue-600" />
              Facebook URL
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border rounded-lg"
              value={urls.facebook}
              onChange={(e) => setUrls({ ...urls, facebook: e.target.value })}
              placeholder="https://facebook.com/kokobeo"
            />
          </div>
          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
              <Twitter className="h-5 w-5 mr-2 text-blue-400" />
              Twitter URL
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border rounded-lg"
              value={urls.twitter}
              onChange={(e) => setUrls({ ...urls, twitter: e.target.value })}
              placeholder="https://twitter.com/kokobeo"
            />
          </div>
          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
              <Instagram className="h-5 w-5 mr-2 text-pink-600" />
              Instagram URL
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border rounded-lg"
              value={urls.instagram}
              onChange={(e) => setUrls({ ...urls, instagram: e.target.value })}
              placeholder="https://instagram.com/kokobeo"
            />
          </div>
          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
              <Linkedin className="h-5 w-5 mr-2 text-blue-800" />
              LinkedIn URL
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border rounded-lg"
              value={urls.linkedin}
              onChange={(e) => setUrls({ ...urls, linkedin: e.target.value })}
              placeholder="https://linkedin.com/company/kokobeo"
            />
          </div>
          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
              <Youtube className="h-5 w-5 mr-2 text-red-600" />
              YouTube URL
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border rounded-lg"
              value={urls.youtube}
              onChange={(e) => setUrls({ ...urls, youtube: e.target.value })}
              placeholder="https://youtube.com/c/kokobeo"
            />
          </div>
          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
              <Music className="h-5 w-5 mr-2 text-black" />
              TikTok URL
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border rounded-lg"
              value={urls.tiktok}
              onChange={(e) => setUrls({ ...urls, tiktok: e.target.value })}
              placeholder="https://tiktok.com/@kokobeo"
            />
          </div>
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={handleCancel} disabled={isLoading}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialMediaModal;
